import { forwardRef } from "react";
import { initials, colorFor } from "./helpers";

const W = 900;

function Avatar({ id, name, size = 30 }) {
  const c = colorFor(id);
  return (
    <span
      style={{
        width: `${size}px`, height: `${size}px`, borderRadius: "50%",
        background: `linear-gradient(135deg,${c},${c}cc)`, color: "#04120b",
        fontWeight: 800, fontSize: `${size * 0.4}px`,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'Barlow Condensed', sans-serif", flexShrink: 0,
      }}
    >
      {initials(name)}
    </span>
  );
}

export const MatchdayExportCard = forwardRef(({ matchday, playersMap, votes = {} }, ref) => {
  if (!matchday) return <div ref={ref} />;
  const dateFmt = (() => {
    try {
      return new Date(matchday.date).toLocaleDateString("it-IT", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
      });
    } catch {
      return matchday.date || "";
    }
  })();

  const teamRow = (ids, win, score, av = {}) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "6px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
        <div style={{ display: "flex" }}>
          {ids.map((id, i) => (
            <span key={id} style={{ marginLeft: i ? "-8px" : 0 }}>
              <Avatar id={id} name={playersMap[id]?.name} />
            </span>
          ))}
        </div>
        <span style={{ color: win ? "#ffffff" : "#94a3b8", fontWeight: win ? 700 : 500, fontSize: "17px" }}>
          {ids.map((id, i) => (
            <span key={id}>
              {i ? " / " : ""}{playersMap[id]?.short_name}
              {av[id] && <span style={{ color: "#FBBF24", fontFamily: "'JetBrains Mono', monospace", fontSize: "14px", fontWeight: 800 }}> ★{av[id].avg.toFixed(1)}</span>}
            </span>
          ))}
        </span>
      </div>
      {score !== null && (
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 900, fontSize: "26px", color: "#10B981" }}>
          {score}
        </span>
      )}
    </div>
  );

  return (
    <div
      ref={ref}
      style={{
        position: "fixed", left: "-10000px", top: 0, width: `${W}px`,
        background: "#090D16", padding: "40px", boxSizing: "border-box",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "10px" }}>
        <div
          style={{
            width: "56px", height: "56px", borderRadius: "16px",
            background: "linear-gradient(135deg,#10B981,#047857)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#04120b", fontWeight: 900, fontSize: "28px",
            fontFamily: "'Barlow Condensed', sans-serif",
          }}
        >
          AP
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: "#10B981", fontSize: "14px", letterSpacing: "4px" }}>AMERICAN PADEL 2026</div>
          <div
            style={{
              fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, textTransform: "uppercase",
              fontSize: "38px", color: "#ffffff", lineHeight: 1,
            }}
          >
            Giornata {matchday.number}
          </div>
        </div>
        <div style={{ color: "#64748B", fontSize: "14px", textTransform: "capitalize", textAlign: "right", maxWidth: "200px" }}>
          {dateFmt}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "20px" }}>
        {matchday.matches
          .slice()
          .sort((a, b) => a.court - b.court)
          .map((m) => {
            const sets = m.sets || [];
            const s1 = sets.filter((s) => s.g1 > s.g2).length;
            const s2 = sets.filter((s) => s.g2 > s.g1).length;
            const done = m.status === "completed";
            return (
              <div
                key={m.id}
                style={{
                  background: "#111a2e", border: "1px solid #1f2b45", borderRadius: "16px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "10px 18px", background: "linear-gradient(90deg,#132038,#0f2a24)",
                    borderBottom: "1px solid #1f2b45",
                  }}
                >
                  <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, textTransform: "uppercase", fontSize: "20px", color: "#ffffff" }}>
                    Campo {m.court}
                  </span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "14px", color: done ? "#64748B" : "#F59E0B" }}>
                    {done ? sets.map((s) => `${s.g1}-${s.g2}`).join("  ") : "Da giocare"}
                  </span>
                </div>
                <div style={{ padding: "8px 18px" }}>
                  {teamRow(m.team1, done && s1 > s2, done ? s1 : null, votes[m.id]?.averages)}
                  <div style={{ height: "1px", background: "#1f2b45" }} />
                  {teamRow(m.team2, done && s2 > s1, done ? s2 : null, votes[m.id]?.averages)}
                  {m.notes && (
                    <div style={{ color: "#64748B", fontSize: "13px", fontStyle: "italic", paddingTop: "6px" }}>
                      {m.notes}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>

      <div
        style={{
          marginTop: "22px", display: "flex", justifyContent: "space-between",
          color: "#64748B", fontSize: "13px", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "1px",
        }}
      >
        <span>AMERICANA · STAGIONE 2026</span>
        <span>V2-0=3 · V2-1=2 · P1-2=1 · P0-2=0</span>
      </div>
    </div>
  );
});

MatchdayExportCard.displayName = "MatchdayExportCard";
